// src/pages/TeacherChatPage.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import TeacherNavbar from "../components/TeacherNavbar";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Divider,
  List,
  ListItemButton,
  Avatar,
  Badge,
  TextField,
  Button,
  IconButton,
  Tooltip,
  InputAdornment,
  Chip,
  Menu,
  MenuItem,
  Popover,
  CircularProgress,
  useMediaQuery
} from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import SendIcon from "@mui/icons-material/Send";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import AttachFileRoundedIcon from "@mui/icons-material/AttachFileRounded";
import KeyboardVoiceRoundedIcon from "@mui/icons-material/KeyboardVoiceRounded";
import EmojiEmotionsOutlinedIcon from "@mui/icons-material/EmojiEmotionsOutlined";
import StopRoundedIcon from "@mui/icons-material/StopRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { getAuth } from "firebase/auth";
import {
  collection,
  doc,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  addDoc,
  updateDoc,
  deleteDoc,
  startAfter,
  serverTimestamp,
  increment,
  getDocs, 
  setDoc, 
  documentId,
  writeBatch
} from "firebase/firestore";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { db, storage } from "../firebaseConfig";
import EmailIcon from '@mui/icons-material/Email';
import { useTranslation } from "react-i18next";
import EmojiPicker from "emoji-picker-react";

/**
 * UI goals:
 * - No grey background page; everything blends into a clean white layout like the reference screenshot
 * - Left sidebar: search + inbox tabs + messages list
 * - Right: chat header + message stream + message composer input (send messages)
 * - No "Create group", no group section
 * - Real-time Firestore listeners for conversations and messages
 *
 * Firestore assumptions (recommended):
 * conversations/{id} fields:
 *  - teacherId, studentId, studentName, studentPhotoURL
 *  - lastMessageText, lastMessageAt, unreadCountTeacher
 *
 * conversations/{id}/messages/{mid} fields:
 *  - senderId, text, createdAt
 */

function ChatBubble({ isMine, children, time, status, progress, label, seen }) {
  const { t } = useTranslation();
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: isMine ? "flex-end" : "flex-start",
        maxWidth: "100%",
        minWidth: 0, // IMPORTANT: allow shrink inside flex parents
      }}
    >
      {(label || time) && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            width: "100%",
            maxWidth: { xs: "96%", sm: "82%", md: "78%" },
            mb: 0.6,
            px: 0.2,
            alignSelf: isMine ? "flex-end" : "flex-start",
          }}
        >
          <Typography sx={{ fontSize: 12, color: "#6b7280", fontWeight: 700 }}>
            {label || (isMine ? t("You") : "")}
          </Typography>
          {!!time && (
            <Typography sx={{ fontSize: 12, color: "#9ca3af", fontWeight: 600 }}>
              {time}
            </Typography>
          )}
        </Box>
      )}

      <Box
        sx={{
          position: "relative",

          // IMPORTANT: better flex + wrapping behavior
          display: "inline-block",
          maxWidth: { xs: "96%", sm: "82%", md: "78%" },
          minWidth: 0,

          px: 2.2,
          py: 1.35,

          // Softer radius like the reference
          borderRadius: 6,

          fontSize: 14,
          lineHeight: 1.55,

          // IMPORTANT: robust wrapping for long URLs/strings
          whiteSpace: "pre-wrap",
          overflowWrap: "anywhere",
          wordBreak: "break-word",
          hyphens: "auto",

          bgcolor: isMine ? "#6f4bd8" : "#eef0f6",
          color: isMine ? "#ffffff" : "#111827",
          boxShadow: "0 2px 6px rgba(15, 23, 42, 0.08)",
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.6 }}>
          <Box sx={{ minWidth: 0 }}>{children}</Box>
          {status === "uploading" && (
            <Typography sx={{ fontSize: 11, color: "#6b7280" }}>
              {progress != null ? t("Uploading {{p}}%", { p: Math.max(1, Math.min(100, Math.round(progress))) }) : t("Sending...")}
            </Typography>
          )}
          {status === "failed" && (
            <Typography sx={{ fontSize: 11, color: "#ef4444" }}>{t("Failed to send")}</Typography>
          )}
        </Box>
      </Box>
      {isMine && seen && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            mt: 0.5,
            px: 0.2,
            alignSelf: "flex-end",
          }}
        >
          <Typography sx={{ fontSize: 10, color: "#9ca3af" }}>{t("Seen")}</Typography> <VisibilityOutlinedIcon sx={{ fontSize: 14, color: "#9ca3af" }} />
        </Box>
      )}
    </Box>
  );
}

const initialsFromName = (fullName = "") => {
  const parts = fullName.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "?";
  const first = parts[0][0] || "";
  const last = parts.length > 1 ? parts[parts.length - 1][0] : "";
  const joined = `${first}${last || ""}`.trim();
  return joined ? joined.toUpperCase() : "?";
};

const AVATAR_COLORS = [
  { bg: "#5295ef", color: "#ffffff" },
  { bg: "#f281aa", color: "#ffffff" },
  { bg: "#05d9c5", color: "#ffffff" },
  { bg: "#5866cd", color: "#ffffff" },
];

const avatarColorsForName = (fullName = "") => {
  if (!fullName) return AVATAR_COLORS[0];
  const hash = fullName
    .split("")
    .reduce((acc, ch) => acc + ch.charCodeAt(0), 0);
  return AVATAR_COLORS[hash % AVATAR_COLORS.length];
};

const MAX_IMAGE_BYTES = 25 * 1024 * 1024;
const MAX_AUDIO_BYTES = 20 * 1024 * 1024;
const MAX_AUDIO_MS = 120 * 1000;
const MAX_VIDEO_BYTES = 1024 * 1024 * 1024;

function TeacherChatPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const auth = getAuth();
  const isRtl = (i18n.resolvedLanguage || i18n.language || "").toLowerCase().startsWith("ar");
  const teacherUid = auth.currentUser?.uid;
  const [teacherGroupIds, setTeacherGroupIds] = useState([]);
  const isMobile = useMediaQuery("(max-width:900px)");
  const [mobileView, setMobileView] = useState("list"); // "list" | "chat"
  const [allStudents, setAllStudents] = useState([]); // unified list
  const [search, setSearch] = useState("");

  const [conversations, setConversations] = useState([]);
  const [activeConversation, setActiveConversation] = useState(null);

  const [messages, setMessages] = useState([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [messagesHasMore, setMessagesHasMore] = useState(true);
  const [messagesCursor, setMessagesCursor] = useState(null);
  const [initialMessagesLoaded, setInitialMessagesLoaded] = useState(false);
  const [messageInput, setMessageInput] = useState("");
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({}); // messageId -> percent
  const [uploadPreviews, setUploadPreviews] = useState({}); // messageId -> { url, type }
  const [recording, setRecording] = useState(false);
  const [recordSeconds, setRecordSeconds] = useState(0);
  const [messageMenuAnchor, setMessageMenuAnchor] = useState(null);
  const [activeMessageId, setActiveMessageId] = useState(null);
  const [activeMessageSenderId, setActiveMessageSenderId] = useState(null);
  const [emojiAnchorEl, setEmojiAnchorEl] = useState(null);

  const messagesEndRef = useRef(null);
  const messagesListRef = useRef(null);
  const shouldScrollToBottomRef = useRef(false);
  const isAtBottomRef = useRef(true);
  const prependAdjustRef = useRef(null);
  const fileInputRef = useRef(null);
  const composerInputRef = useRef(null);
  const mountedRef = useRef(true);
  const recorderRef = useRef(null);
  const recordChunksRef = useRef([]);
  const recordSecondsRef = useRef(0);
  const recordTimerRef = useRef(null);
  const recordTimeoutRef = useRef(null);
  const recordStreamRef = useRef(null);
  const recordCancelRef = useRef(false);
  const recordFinalizedRef = useRef(false);
  const isEmojiPickerOpen = Boolean(emojiAnchorEl);

  const handleEmojiPick = (emojiData) => {
    const emoji = emojiData?.emoji || "";
    if (!emoji) return;

    const inputEl = composerInputRef.current;
    if (inputEl && typeof inputEl.selectionStart === "number") {
      const start = inputEl.selectionStart;
      const end = inputEl.selectionEnd ?? start;
      const next = `${messageInput.slice(0, start)}${emoji}${messageInput.slice(end)}`;
      setMessageInput(next);

      requestAnimationFrame(() => {
        inputEl.focus();
        const cursor = start + emoji.length;
        inputEl.setSelectionRange(cursor, cursor);
      });
      return;
    }

    setMessageInput((prev) => `${prev}${emoji}`);
  };

  const conversationsQuery = useMemo(() => {
    if (!teacherUid) return null;
    return query(
      collection(db, "conversations"),
      where("teacherId", "==", teacherUid),
      orderBy("lastMessageAt", "desc")
    );
  }, [teacherUid]);

  const inboxUnreadTotal = useMemo(() => {
    return conversations.reduce((sum, c) => sum + Number(c.unreadCountTeacher || 0), 0);
  }, [conversations]);


  const allStudentRows = useMemo(() => {
    // Map existing conversations by studentId for fast lookup
    const convoByStudent = new Map();
    conversations.forEach((c) => {
      if (c.studentId) convoByStudent.set(c.studentId, c);
    });

    // Merge: every student appears, convo data if exists
    const merged = allStudents.map((s) => {
      const convo = convoByStudent.get(s.studentId);
      return {
        studentId: s.studentId,
        studentName: s.studentName,
        studentPhotoURL: s.studentPhotoURL,
        conversation: convo || null,
        lastMessageText: convo?.lastMessageText || "",
        lastMessageAt: convo?.lastMessageAt || null,
        unreadCountTeacher: convo?.unreadCountTeacher || 0,
        lastMessageType: convo?.lastMessageType || "text",
        convoId: convo?.id || null,
      };
    });

    // Sort: those with lastMessageAt first, then by name
    merged.sort((a, b) => {
      const aT = a.lastMessageAt?.toMillis?.() || 0;
      const bT = b.lastMessageAt?.toMillis?.() || 0;
      if (bT !== aT) return bT - aT;
      return (a.studentName || "").localeCompare(b.studentName || "");
    });

    return merged;
  }, [allStudents, conversations]);


  const filteredStudents = useMemo(() => {
    const s = search.trim().toLowerCase();
    if (!s) return allStudentRows;

    return allStudentRows.filter((row) => {
      const name = (row.studentName || "").toLowerCase();
      const last = (row.lastMessageText || "").toLowerCase();
      return name.includes(s) || last.includes(s);
    });
  }, [allStudentRows, search]);

  const previewText = (row) => {
    if (row.lastMessageType && row.lastMessageType !== "text") {
      return labelForType(row.lastMessageType, row.lastMessageText);
    }
    return row.lastMessageText ? row.lastMessageText : t("No messages yet");
  };


  const safeTime = (ts) => {
    try {
      if (!ts) return "";
      if (typeof ts.toDate === "function") {
        const d = ts.toDate();
        return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
      }
      const d = typeof ts === "number" ? new Date(ts) : new Date(ts);
      if (Number.isNaN(d.getTime())) return "";
      return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    } catch {
      return "";
    }
  };

  const safeDateSeparator = (ts) => {
    try {
      if (!ts) return "";
      const d = typeof ts.toDate === "function" ? ts.toDate() : new Date(ts);
      if (Number.isNaN(d.getTime())) return "";
      return d.toLocaleDateString([], { weekday: "short", month: "short", day: "numeric" });
    } catch {
      return "";
    }
  };

  const labelForType = (type, fallback) => {
    if (type === "image") return t("Photo");
    if (type === "video") return t("Video");
    if (type === "audio") return t("Voice message");
    return fallback || t("Message");
  };

  // Realtime inbox
  useEffect(() => {
    if (!conversationsQuery) return;

    const unsub = onSnapshot(
      conversationsQuery,
      (snap) => {
        const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setConversations(list);

        // Keep selected convo fresh if updated
        if (activeConversation?.id) {
          const updated = list.find((x) => x.id === activeConversation.id);
          if (updated) setActiveConversation(updated);
        }
      },
      (err) => console.error("Failed to fetch conversations:", err)
    );

    return () => unsub();
  }, [conversationsQuery, activeConversation?.id]);

  const scrollToBottom = (behavior = "auto") => {
    const el = messagesListRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior });
  };

  const normalizeMessageTs = (msg) => {
    const ts = msg?.createdAt;
    if (!ts) return 0;
    if (typeof ts.toMillis === "function") return ts.toMillis();
    if (typeof ts.seconds === "number") return ts.seconds * 1000;
    return 0;
  };

  const fetchMoreMessages = async () => {
    if (!activeConversation?.id || messagesLoading || !messagesHasMore || !messagesCursor) return;
    const el = messagesListRef.current;
    if (el) {
      prependAdjustRef.current = { prevHeight: el.scrollHeight, prevTop: el.scrollTop };
    }
    setMessagesLoading(true);
    try {
      const msgRef = collection(db, "conversations", activeConversation.id, "messages");
      const q = query(msgRef, orderBy("createdAt", "desc"), startAfter(messagesCursor), limit(10));
      const snap = await getDocs(q);
      const docs = snap.docs;
      const older = docs.map((d) => ({ id: d.id, ...d.data() })).reverse();
      setMessages((prev) => [...older, ...prev]);
      setMessagesCursor(docs[docs.length - 1] || messagesCursor);
      setMessagesHasMore(docs.length === 10);
    } catch (err) {
      console.error("Failed to fetch older messages:", err);
    } finally {
      setMessagesLoading(false);
    }
  };

  const handleMessagesScroll = () => {
    const el = messagesListRef.current;
    if (!el) return;
    const bottomGap = el.scrollHeight - el.scrollTop - el.clientHeight;
    isAtBottomRef.current = bottomGap < 40;
    if (el.scrollTop <= 40 && messagesHasMore && !messagesLoading) {
      fetchMoreMessages();
    }
  };

  // Initial load + live recent updates
  useEffect(() => {
    if (!activeConversation?.id) {
      setMessages([]);
      setMessagesHasMore(true);
      setMessagesCursor(null);
      setInitialMessagesLoaded(false);
      return;
    }

    let cancelled = false;
    let unsub = () => {};

    const loadInitial = async () => {
      setMessagesLoading(true);
      try {
        const msgRef = collection(db, "conversations", activeConversation.id, "messages");
        const q = query(msgRef, orderBy("createdAt", "desc"), limit(10));
        const snap = await getDocs(q);
        if (cancelled) return;
        const docs = snap.docs;
        const list = docs.map((d) => ({ id: d.id, ...d.data() })).reverse();
        setMessages(list);
        setMessagesCursor(docs[docs.length - 1] || null);
        setMessagesHasMore(docs.length === 10);
        setInitialMessagesLoaded(true);
        shouldScrollToBottomRef.current = true;
      } catch (err) {
        console.error("Failed to fetch initial messages:", err);
      } finally {
        if (!cancelled) setMessagesLoading(false);
      }
    };

    loadInitial();

    const msgRef = collection(db, "conversations", activeConversation.id, "messages");
    const recentQuery = query(msgRef, orderBy("createdAt", "desc"), limit(10));
    unsub = onSnapshot(
      recentQuery,
      (snap) => {
        const recent = snap.docs.map((d) => ({ id: d.id, ...d.data() })).reverse();
        const wasAtBottom = isAtBottomRef.current;
        setMessages((prev) => {
          if (!prev.length) return recent;
          const map = new Map(prev.map((m) => [m.id, m]));
          recent.forEach((m) => map.set(m.id, m));
          const merged = Array.from(map.values());
          merged.sort((a, b) => normalizeMessageTs(a) - normalizeMessageTs(b));
          return merged;
        });
        if (wasAtBottom) {
          shouldScrollToBottomRef.current = true;
        }
      },
      (err) => console.error("Failed to fetch recent messages:", err)
    );

    return () => {
      cancelled = true;
      unsub();
    };
  }, [activeConversation?.id]);

  // Mark read for teacher
  useEffect(() => {
    const markRead = async () => {
      if (!activeConversation?.id) return;
      try {
        await updateDoc(doc(db, "conversations", activeConversation.id), {
          unreadCountTeacher: 0,
        });
      } catch (e) {
        console.error("Failed to mark read:", e);
      }
    };
    markRead();
  }, [activeConversation?.id]);

  // Mark student messages as seen by teacher when viewing
  useEffect(() => {
    if (!activeConversation?.id || !teacherUid || !messages.length) return;
    const unseen = messages.filter(
      (m) => m.senderId && m.senderId !== teacherUid && !m.seenByTeacherAt
    );
    if (!unseen.length) return;
    const markSeen = async () => {
      try {
        const batch = writeBatch(db);
        unseen.forEach((m) => {
          batch.update(doc(db, "conversations", activeConversation.id, "messages", m.id), {
            seenByTeacherAt: serverTimestamp(),
          });
        });
        await batch.commit();
      } catch (e) {
        console.error("Failed to mark teacher seen:", e);
      }
    };
    markSeen();
  }, [messages, activeConversation?.id, teacherUid]);

  // Adjust scroll after prepending older messages or when at bottom
  useEffect(() => {
    if (!initialMessagesLoaded) return;
    const el = messagesListRef.current;
    if (!el) return;
    if (prependAdjustRef.current) {
      const { prevHeight, prevTop } = prependAdjustRef.current;
      prependAdjustRef.current = null;
      requestAnimationFrame(() => {
        const newHeight = el.scrollHeight;
        el.scrollTop = newHeight - prevHeight + prevTop;
      });
      return;
    }
    if (shouldScrollToBottomRef.current) {
      shouldScrollToBottomRef.current = false;
      requestAnimationFrame(() => scrollToBottom("auto"));
    }
  }, [messages, initialMessagesLoaded]);


  useEffect(() => {
  const fetchTeacherGroups = async () => {
      try {
      const user = auth.currentUser;
      if (!user) return;

      const qTeacher = query(collection(db, "teachers"), where("uid", "==", user.uid));
      const snap = await getDocs(qTeacher);

      if (!snap.empty) {
          const t = snap.docs[0].data();
          setTeacherGroupIds(Array.isArray(t.groupIds) ? t.groupIds : []);
      } else {
          setTeacherGroupIds([]);
      }
      } catch (e) {
      console.error("Failed to fetch teacher groupIds:", e);
      setTeacherGroupIds([]);
      }
  };

  fetchTeacherGroups();
  }, [auth]);


  useEffect(() => {
    if (!teacherGroupIds.length) return;
    fetchAllTeacherStudents(teacherGroupIds);
  }, [teacherGroupIds]);


  const fetchAllTeacherStudents = async (groupIds) => {
    try {
      if (!groupIds?.length) {
        setAllStudents([]);
        return;
      }

      // 1) Collect all studentIds from all groups
      const studentIdSet = new Set();

      for (const gid of groupIds) {
        const gRef = doc(db, "groups", gid);
        const gSnap = await getDocs(query(collection(db, "groups"), where(documentId(), "==", gid)));
        gSnap.forEach((d) => {
          const g = d.data() || {};
          const ids = Array.isArray(g.studentIds) ? g.studentIds : [];
          ids.forEach((sid) => studentIdSet.add(sid));
        });
      }

      const studentIds = Array.from(studentIdSet);
      if (studentIds.length === 0) {
        setAllStudents([]);
        return;
      }

      // 2) Fetch students docs by chunks of 10 (Firestore "in" limit)
      const chunks = [];
      for (let i = 0; i < studentIds.length; i += 10) {
        chunks.push(studentIds.slice(i, i + 10));
      }

      const all = [];
      for (const chunk of chunks) {
        const qStudents = query(collection(db, "students"), where(documentId(), "in", chunk));
        const sSnap = await getDocs(qStudents);

        sSnap.forEach((d) => {
          const s = d.data() || {};
          const firstName = s.firstName || "";
          const lastName = s.lastName || "";

          all.push({
            studentId: d.id,
            studentName: `${firstName} ${lastName}`.trim() || t("Student"),
            studentPhotoURL: s.photoURL || s.imageUrl || "",
          });
        });
      }

      setAllStudents(all);
    } catch (e) {
      console.error("Failed to fetch all teacher students:", e);
      setAllStudents([]);
    }
  };



  const handleSend = async () => {
    if (!teacherUid || !activeConversation?.id) return;

    const text = messageInput.trim();
    if (!text) return;

    setSending(true);
    try {
      const convoRef = doc(db, "conversations", activeConversation.id);
      const msgRef = collection(db, "conversations", activeConversation.id, "messages");

      await addDoc(msgRef, {
        senderId: teacherUid,
        text,
        createdAt: serverTimestamp(),
      });

      await updateDoc(convoRef, {
        lastMessageText: text,
        lastMessageAt: serverTimestamp(),
        lastSenderId: teacherUid,
        lastMessageType: "text",
        unreadCountStudent: increment(1),
      });

      setMessageInput("");
    } catch (e) {
      console.error("Failed to send message:", e);
    } finally {
      setSending(false);
    }
  };

  const handleUploadFile = async (file, durationMs = null) => {
    if (!teacherUid || !activeConversation?.id || !file) return;

    const mime = file.type || "";
    const isImage = mime.startsWith("image/");
    const isVideo = mime.startsWith("video/");
    const isAudio = mime.startsWith("audio/");
    const type = isImage ? "image" : isVideo ? "video" : isAudio ? "audio" : "text";

    if (!isImage && !isVideo && !isAudio) {
      console.warn("Unsupported file type");
      return;
    }

    if (isVideo && file.size > MAX_VIDEO_BYTES) {
      window.alert(t("Video is too large (max 1GB)."));
      return;
    }
    if (isImage && file.size > MAX_IMAGE_BYTES) {
      window.alert(t("Image is too large."));
      return;
    }
    if (isAudio && file.size > MAX_AUDIO_BYTES) {
      window.alert(t("Audio file is too large."));
      return;
    }
    if (isAudio && durationMs && durationMs > MAX_AUDIO_MS) {
      window.alert(t("Audio is longer than 120 seconds."));
      return;
    }

    const createPreviewUrl = () => {
      if (isImage || isVideo) {
        return URL.createObjectURL(file);
      }
      return "";
    };

    const cleanupPreview = (id) => {
      setUploadPreviews((prev) => {
        const existing = prev[id];
        if (existing?.url) {
          URL.revokeObjectURL(existing.url);
        }
        const next = { ...prev };
        delete next[id];
        return next;
      });
    };

    try {
      setUploading(true);
      const convoRef = doc(db, "conversations", activeConversation.id);
      const msgRef = collection(db, "conversations", activeConversation.id, "messages");
      const newMsgRef = doc(msgRef);
      const previewUrl = createPreviewUrl();

      await setDoc(newMsgRef, {
        senderId: teacherUid,
        type,
        text: "",
        url: "",
        thumbnailUrl: "",
        mimeType: mime,
        size: file.size || 0,
        durationMs: durationMs || null,
        status: "uploading",
        createdAt: serverTimestamp(),
      });
      if (previewUrl) {
        setUploadPreviews((prev) => ({
          ...prev,
          [newMsgRef.id]: { url: previewUrl, type },
        }));
      }

      const storagePath = `conversations/${activeConversation.id}/${newMsgRef.id}/${file.name}`;
      const storageRef = ref(storage, storagePath);
      const uploadTask = uploadBytesResumable(storageRef, file);

      uploadTask.on(
        "state_changed",
        (snapshot) => {
          if (!snapshot?.totalBytes) return;
          const pct = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          setUploadProgress((prev) => ({ ...prev, [newMsgRef.id]: pct }));
        },
        async (error) => {
          console.error("Upload failed:", error);
          try {
            await updateDoc(newMsgRef, { status: "failed" });
          } catch {
            /* ignore */
          }
          cleanupPreview(newMsgRef.id);
          setUploadProgress((prev) => {
            const cp = { ...prev };
            delete cp[newMsgRef.id];
            return cp;
          });
          setUploading(false);
        },
        async () => {
          try {
            const url = await getDownloadURL(storageRef);
            await updateDoc(newMsgRef, {
              url,
              status: "sent",
            });

            await updateDoc(convoRef, {
              lastMessageText: labelForType(type, ""),
              lastMessageType: type,
              lastMessageAt: serverTimestamp(),
              lastSenderId: teacherUid,
              unreadCountStudent: increment(1),
            });
          } catch (e) {
            console.error("Failed to finalize upload:", e);
            await updateDoc(newMsgRef, { status: "failed" });
          }
          cleanupPreview(newMsgRef.id);
          setUploadProgress((prev) => {
            const cp = { ...prev };
            delete cp[newMsgRef.id];
            return cp;
          });
          setUploading(false);
        }
      );
    } catch (e) {
      console.error("Failed to upload file:", e);
      setUploading(false);
    }
  };

  const handleAttachmentChange = (event) => {
    const file = event.target.files && event.target.files[0];
    if (file) {
      handleUploadFile(file);
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const clearRecordingTimers = () => {
    if (recordTimerRef.current) {
      clearInterval(recordTimerRef.current);
      recordTimerRef.current = null;
    }
    if (recordTimeoutRef.current) {
      clearTimeout(recordTimeoutRef.current);
      recordTimeoutRef.current = null;
    }
  };

  const stopRecordingStream = () => {
    if (recordStreamRef.current) {
      recordStreamRef.current.getTracks().forEach((t) => t.stop());
      recordStreamRef.current = null;
    }
  };

  const stopRecording = (options = {}) => {
    if (!recorderRef.current) return;
    recordCancelRef.current = options.cancel === true;
    recordFinalizedRef.current = false;
    clearRecordingTimers();
    try {
        if (recorderRef.current.state !== "inactive") {
          recorderRef.current.stop();
        }
    } catch {
      // ignore
    }
    if (options.cancel) {
      recordChunksRef.current = [];
      setRecording(false);
      setRecordSeconds(0);
      recordSecondsRef.current = 0;
      stopRecordingStream();
      return;
    }

    // Fallback if stop event never fires
    setTimeout(() => {
      if (recordFinalizedRef.current || recordCancelRef.current) return;
      if (!recordChunksRef.current.length) return;
      try {
        const mime = recorderRef.current?.mimeType || "audio/webm";
        const blob = new Blob(recordChunksRef.current, { type: mime });
        const file = new File([blob], `voice-${Date.now()}.webm`, { type: blob.type || "audio/webm" });
        recordFinalizedRef.current = true;
        handleUploadFile(file, Math.min(recordSecondsRef.current, MAX_AUDIO_MS / 1000) * 1000);
      } catch (err) {
        console.error("Fallback finalize failed:", err);
      } finally {
        setRecording(false);
        setRecordSeconds(0);
        recordSecondsRef.current = 0;
        recordChunksRef.current = [];
        stopRecordingStream();
      }
    }, 1200);
  };

  const startRecording = async () => {
    if (recording) return;
    if (!activeConversation?.id) return;

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      recordStreamRef.current = stream;
      const rec = new MediaRecorder(stream);
      recorderRef.current = rec;
      recordChunksRef.current = [];
      recordCancelRef.current = false;
      recordFinalizedRef.current = false;
      recordSecondsRef.current = 0;
      setRecordSeconds(0);

      rec.ondataavailable = (e) => {
        if (e.data?.size > 0) recordChunksRef.current.push(e.data);
      };

      rec.onstop = () => {
        clearRecordingTimers();
        stopRecordingStream();
        if (!mountedRef.current) return;
        setRecording(false);
        const wasCancelled = recordCancelRef.current;
        recordCancelRef.current = false;
        recordFinalizedRef.current = true;
        const durationMs = Math.min(recordSecondsRef.current, MAX_AUDIO_MS / 1000) * 1000;
        const blob = new Blob(recordChunksRef.current, { type: rec.mimeType || "audio/webm" });
        recordChunksRef.current = [];
        setRecordSeconds(0);
        recordSecondsRef.current = 0;
        if (wasCancelled) return;
        try {
          const file = new File([blob], `voice-${Date.now()}.webm`, { type: blob.type || "audio/webm" });
          handleUploadFile(file, durationMs);
        } catch (err) {
          console.error("Failed to build voice file:", err);
        }
      };

      rec.start();
      setRecording(true);
      recordTimerRef.current = setInterval(() => {
        recordSecondsRef.current += 1;
        setRecordSeconds(recordSecondsRef.current);
        if (recordSecondsRef.current >= MAX_AUDIO_MS / 1000) {
          stopRecording();
        }
      }, 1000);

      recordTimeoutRef.current = setTimeout(() => {
        stopRecording();
      }, MAX_AUDIO_MS);
    } catch (e) {
      console.error("Failed to start recording:", e);
      stopRecording({ cancel: true });
      setRecording(false);
      setRecordSeconds(0);
      recordSecondsRef.current = 0;
      window.alert(t("Unable to start recording. Please check microphone permissions."));
    }
  };

  const cancelRecording = () => {
    if (!recording) return;
    stopRecording({ cancel: true });
  };

  useEffect(() => {
    return () => {
      mountedRef.current = false;
      stopRecording({ cancel: true });
      clearRecordingTimers();
      stopRecordingStream();
    };
  }, []);

  // Optional: simple date grouping (visual)
  const groupedMessages = useMemo(() => {
    const out = [];
    let lastSep = "";

    for (const m of messages) {
      const sep = safeDateSeparator(m.createdAt);
      if (sep && sep !== lastSep) {
        out.push({ type: "sep", id: `sep-${sep}`, label: sep });
        lastSep = sep;
      }
      out.push({ type: "msg", ...m });
    }
    return out;
  }, [messages]);


  const renderMessageContent = (msg) => {
    const type = msg.type || "text";
    const text = msg.text || "";
    const url = msg.url || "";
    const thumb = msg.thumbnailUrl || "";
    const preview = msg.id ? uploadPreviews[msg.id] : null;
    const progress = msg.id ? uploadProgress[msg.id] : null;

    const renderUploadingOverlay = () => (
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "rgba(15, 23, 42, 0.25)",
        }}
      >
        <CircularProgress
          variant={progress != null ? "determinate" : "indeterminate"}
          value={progress != null ? Math.max(1, Math.min(100, Math.round(progress))) : undefined}
          thickness={4}
          size={54}
          sx={{ color: "#ffffff" }}
        />
      </Box>
    );

    if (type === "image") {
      if (!url && msg.status === "uploading" && preview?.url) {
        return (
          <Box sx={{ maxWidth: 320, position: "relative" }}>
            <Box
              component="img"
              src={preview.url}
              alt={t("Image")}
              sx={{
                width: "100%",
                height: "auto",
                borderRadius: 2,
                border: "1px solid #e5e7eb",
                display: "block",
                filter: "blur(3px)",
                transform: "scale(1.02)",
              }}
            />
            {renderUploadingOverlay()}
          </Box>
        );
      }
      return url ? (
        <Box sx={{ maxWidth: 320 }}>
          <Box
            component="img"
            src={url}
            alt={t("Image")}
            sx={{
              width: "100%",
              height: "auto",
              borderRadius: 2,
              border: "1px solid #e5e7eb",
              display: "block",
            }}
            loading="lazy"
          />
        </Box>
      ) : (
        <Typography sx={{ color: "#6b7280" }}>{t("Image")}</Typography>
      );
    }

    if (type === "video") {
      if (!url && msg.status === "uploading" && preview?.url) {
        return (
          <Box sx={{ maxWidth: 340, position: "relative" }}>
            <Box
              component="video"
              src={preview.url}
              muted
              playsInline
              style={{
                width: "100%",
                borderRadius: 12,
                border: "1px solid #e5e7eb",
                display: "block",
                filter: "blur(3px)",
                transform: "scale(1.02)",
              }}
            />
            {renderUploadingOverlay()}
          </Box>
        );
      }
      return url ? (
        <Box sx={{ maxWidth: 340 }}>
          <Box
            component="video"
            src={url}
            poster={thumb || undefined}
            controls
            style={{ width: "100%", borderRadius: 12, border: "1px solid #e5e7eb" }}
          />
        </Box>
      ) : (
        <Typography sx={{ color: "#6b7280" }}>{t("Video")}</Typography>
      );
    }

    if (type === "audio") {
      return url ? (
        <Box sx={{ minWidth: 220, maxWidth: 320, width: "100%" }}>
          <audio controls style={{ width: "100%", display: "block" }}>
            <track kind="captions" />
            <source src={url} type={msg.mimeType || "audio/mpeg"} />
            {t("Your browser does not support the audio element.")}
          </audio>
        </Box>
      ) : (
        <Typography sx={{ color: "#6b7280" }}>{t("Voice message")}</Typography>
      );
    }

    return (
      <Typography dir="auto" sx={{ whiteSpace: "pre-wrap", textAlign: "start" }}>
        {text}
      </Typography>
    );
  };

  const openOrCreateConversation = async (student) => {
    try {
        if (!teacherUid) return;

        // 1) Find existing convo for teacher+student
        const qConvo = query(
        collection(db, "conversations"),
        where("teacherId", "==", teacherUid),
        where("studentId", "==", student.studentId)
        );

        const snap = await getDocs(qConvo);

        if (!snap.empty) {
        const d = snap.docs[0];
        setActiveConversation({ id: d.id, ...d.data() });
        if (isMobile) setMobileView("chat");
        return;
        }

        // 2) Create new conversation doc (use deterministic ID to avoid duplicates)
        const convoId = `${teacherUid}_${student.studentId}`;
        const convoRef = doc(db, "conversations", convoId);

        await setDoc(
        convoRef,
        {
            teacherId: teacherUid,
            studentId: student.studentId,
            groupId: student.groupId || "",

            participantIds: [teacherUid, student.studentId],

            studentName: student.studentName?.trim() || t("Student"),
            studentPhotoURL: student.studentPhotoURL || "",

            lastMessageText: "",
            lastMessageAt: serverTimestamp(),
            lastSenderId: "",
            lastMessageType: "text",

            unreadCountTeacher: 0,
            unreadCountStudent: 0,
        },
        { merge: true }
        );

        // 3) Open it in UI
        setActiveConversation({
        id: convoId,
        teacherId: teacherUid,
        studentId: student.studentId,
        groupId: student.groupId || "",
        participantIds: [teacherUid, student.studentId],
        studentName: student.studentName?.trim() || t("Student"),
        studentPhotoURL: student.studentPhotoURL || "",
        lastMessageText: "",
        lastMessageAt: null,
        lastSenderId: "",
        lastMessageType: "text",
        unreadCountTeacher: 0,
        unreadCountStudent: 0,
        });

        if (isMobile) setMobileView("chat");
    } catch (e) {
        console.error("openOrCreateConversation failed:", e);
    }
    };

  return (
    <Box
      sx={{
        background: "linear-gradient(180deg, #ffffff 0%, #f9fafb 100%)",
        height: "100vh",
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <Box sx={{ flex: "0 0 auto" }}>
        <TeacherNavbar />
      </Box>

      {/* Whole page is one white blended canvas like the reference */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          width: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Main layout container (no outer card, no grey background) */}
        <Box
          sx={{
            display: "flex",
            flexDirection: isRtl ? "row-reverse" : "row",
            border: "1px solid #e5e7eb",
            borderRadius: 0,
            overflow: "hidden",
            flex: 1,
            minHeight: 0,
            backgroundColor: "#fff",
          }}
        >
          {/* LEFT SIDEBAR */}
          <Box
            sx={{
              width: { xs: "100%", md: 380 },
              borderRight: { xs: "none", md: isRtl ? "none" : "1px solid #e5e7eb" },
              borderLeft: { xs: "none", md: isRtl ? "1px solid #e5e7eb" : "none" },
              display: { xs: mobileView === "list" ? "flex" : "none", md: "flex" },
              flexDirection: "column",
            }}
          >
            {/* Search */}
            <Box
              sx={{
                p: 2,
                display: "flex",
                flexDirection: isRtl ? "row-reverse" : "row",
                alignItems: "center",
                gap: 1,
              }}
            >
              <IconButton
                size="small"
                onClick={() => navigate(-1)}
                aria-label={t("Go back")}
                sx={{ border: "1px solid #e5e7eb", bgcolor: "#ffffff" }}
              >
                <ArrowBackIosNewIcon
                  fontSize="small"
                  sx={{ transform: isRtl ? "rotate(180deg)" : "none" }}
                />
              </IconButton>
              <TextField
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                fullWidth
                placeholder={t("Search")}
                size="small"
                InputProps={{
                  ...(isRtl
                    ? {
                        endAdornment: (
                          <InputAdornment position="end">
                            <SearchRoundedIcon />
                          </InputAdornment>
                        ),
                      }
                    : {
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchRoundedIcon />
                          </InputAdornment>
                        ),
                      }),
                  sx: {
                    direction: isRtl ? "rtl" : "ltr",
                    "& .MuiInputBase-input": { textAlign: isRtl ? "right" : "left" },
                  },
                }}
              />
            </Box>

            <Divider />

            {/* Section title */}
            <Box sx={{ px: 2, py: 1.5 }}>
              <Typography sx={{ fontWeight: 900, color: "#111827" }}>{t("Messages")}</Typography>
            </Box>

            {/* Left content: ALL students (chat exists or not) */}
            <Box sx={{ flex: 1, overflowY: "auto" }}>
              <List sx={{ p: 0 }}>
                {filteredStudents.length === 0 ? (
                  <Box sx={{ p: 2 }}>
                    <Typography sx={{ color: "#6b7280" }}>{t("No students found.")}</Typography>
                  </Box>
                ) : (
                  filteredStudents.map((row) => {
                    const unread = Number(row.unreadCountTeacher || 0);
                    const isActive = activeConversation?.studentId === row.studentId;
                    const avatarTone = avatarColorsForName(row.studentName || t("Student"));

                    return (
                      <ListItemButton
                        key={row.studentId}
                        selected={isActive}
                        onClick={() =>
                          openOrCreateConversation({
                            studentId: row.studentId,
                            studentName: row.studentName,
                            studentPhotoURL: row.studentPhotoURL,
                            groupId: "", // optional
                          })
                        }
                        sx={{
                          px: 2,
                          py: 1.6,
                          borderBottom: "1px solid #f3f4f6",
                          alignItems: "flex-start",
                          direction: isRtl ? "rtl" : "ltr",
                          gap: 1.5,
                          "&.Mui-selected": { backgroundColor: "#f3f4f6" },
                          "&.Mui-selected:hover": { backgroundColor: "#e5e7eb" },
                        }}
                      >
                        <Badge color="primary" badgeContent={unread} invisible={unread <= 0} overlap="circular">
                          <Avatar
                            variant="square"
                            src={row.studentPhotoURL || undefined}
                            alt={row.studentName || t("Student")}
                            sx={{
                              width: 44,
                              height: 44,
                              bgcolor: "#edf1f2",
                              color: "black",
                              fontWeight: 900,
                              borderRadius: 2,
                            }}
                          >
                            {initialsFromName(row.studentName || t("Student"))}
                          </Avatar>
                        </Badge>

                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography
                            sx={{
                              fontWeight: 900,
                              color: "#111827",
                              textAlign: "start",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {row.studentName || t("Student")}
                          </Typography>

                          <Typography
                            sx={{
                              color: "#6b7280",
                              fontSize: 13,
                              mt: 0.4,
                              textAlign: "start",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {previewText(row)}
                          </Typography>
                        </Box>

                        <Typography sx={{ color: "#9ca3af", fontSize: 12, flexShrink: 0, mt: 0.2, textAlign: "start" }}>
                          {safeTime(row.lastMessageAt)}
                        </Typography>
                      </ListItemButton>
                    );
                  })
                )}
              </List>
            </Box>
          </Box>

          {/* RIGHT CHAT AREA */}
          <Box
            sx={{
              flex: 1,
              display: { xs: mobileView === "chat" ? "flex" : "none", md: "flex" },
              flexDirection: "column",
              backgroundColor: "#fff",
            }}
          >
            {!activeConversation ? (
              <Box
                sx={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  textAlign: "center",
                  px: 3,
                  background:
                    "radial-gradient(1200px 600px at 10% -10%, rgba(255, 204, 155, 0.35), transparent 60%), radial-gradient(900px 500px at 100% 0%, rgba(130, 189, 255, 0.35), transparent 60%), #f6f7fb",
                }}
              >
                <EmailIcon
                  sx={{
                    fontSize: 48,
                    color: "#201f1fff",
                    mb: 1.5,
                  }}
                />

                <Typography
                  sx={{
                    fontWeight: 900,
                    fontSize: 18,
                    color: "#111827",
                    mb: 0.7,
                  }}
                >
                  {t("Select a conversation")}
                </Typography>

                <Typography sx={{ color: "#6b7280", maxWidth: 260 }}>
                  {t("Choose a student from the left to view messages.")}
                </Typography>
              </Box>
            ) : (
              <>
                {/* Thread header */}
                <Box
                  sx={{
                    px: 2.5,
                    py: 1.8,
                    display: "flex",
                    flexDirection: isRtl ? "row-reverse" : "row",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1.5,
                  }}
                >
                  <Box sx={{ display: "flex", flexDirection: isRtl ? "row-reverse" : "row", alignItems: "center", gap: 1.5, minWidth: 0 }}>
                    {isMobile && (
                      <IconButton
                        size="small"
                        onClick={() => setMobileView("list")}
                        sx={{ mx: 0.5 }}
                        aria-label={t("Back to list")}
                      >
                        <ArrowBackIosNewIcon
                          fontSize="small"
                          sx={{ transform: isRtl ? "rotate(180deg)" : "none" }}
                        />
                      </IconButton>
                    )}

                    <Avatar
                      variant="square"
                      src={activeConversation.studentPhotoURL || undefined}
                      alt={activeConversation.studentName || t("Student")}
                      sx={{
                        width: 46,
                        height: 46,
                        bgcolor: avatarColorsForName(activeConversation.studentName || t("Student")).bg,
                        color: avatarColorsForName(activeConversation.studentName || t("Student")).color,
                        fontWeight: 900,
                        objectFit: "cover",
                        borderRadius: 6,
                      }}
                    >
                      {initialsFromName(activeConversation.studentName || t("Student"))}
                    </Avatar>

                    <Box sx={{ minWidth: 0 }}>
                      <Typography
                        sx={{
                          fontWeight: 900,
                          color: "#111827",
                          textAlign: "start",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {activeConversation.studentName || t("Student")}
                      </Typography>

                      <Box sx={{ display: "flex", flexDirection: isRtl ? "row-reverse" : "row", alignItems: "center", gap: 1, mt: 0.4 }}>
                        <Typography sx={{ color: "#6b7280", fontSize: 13 }}>
                          {t("Direct messages")}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>

                  <Box sx={{ display: "flex", flexDirection: isRtl ? "row-reverse" : "row", alignItems: "center", gap: 0.5 }}>

                    <Tooltip title={t("Info")}>
                      <IconButton size="small">
                        <InfoOutlinedIcon />
                      </IconButton>
                    </Tooltip>

                    <Tooltip title={t("More")}>
                      <IconButton size="small">
                        <MoreHorizIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>

                <Divider />

                {/* Messages stream (white like reference) */}
                <Box
                  sx={{
                    flex: 1,
                    overflowY: "auto",
                    overflowX: "hidden",
                    px: 3,
                    py: 2.5,
                    backgroundColor: "#fcfcfe",
                  }}
                  ref={messagesListRef}
                  onScroll={handleMessagesScroll}
                >
                  {groupedMessages.length === 0 ? (
                    <Typography sx={{ color: "#6b7280" }}>{t("No messages yet.")}</Typography>
                  ) : (
                    groupedMessages.map((item) => {
                      if (item.type === "sep") {
                        return (
                          <Box
                            key={item.id}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              my: 2,
                            }}
                          >
                            <Typography
                              sx={{
                                backgroundColor: '#ffffffff',
                                color: "#888686ff",
                                fontSize: 12,
                                fontWeight: 800,
                                px: 2,
                                py: 0.6,
                                borderRadius: "999px",
                                border: "1px solid #e5e7eb",
                              }}
                            >
                              {item.label}
                            </Typography>
                          </Box>
                        );
                      }

                      const isMine = item.senderId === teacherUid;
                      const messageLabel = isMine ? t("You") : (activeConversation?.studentName || t("Student"));
                      const messageTime = safeTime(item.createdAt);
                      const bubbleTone = avatarColorsForName(activeConversation?.studentName || t("Student"));
                      const messageType = item.type || "text";
                      const isAttachment = ["image", "video", "audio"].includes(messageType);
                      const isAudioAttachment = messageType === "audio";
                      const isSeenByStudent = isMine && !!item.seenByStudentAt;
                      const messageBody = isAttachment ? (
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: isMine ? "flex-end" : "flex-start",
                            maxWidth: "100%",
                            minWidth: 0,
                            pr: isMine && isAudioAttachment ? { xs: 0, md: 1.5 } : 0,
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              width: "100%",
                              maxWidth: { xs: "96%", sm: "82%", md: "78%" },
                              mb: 0.6,
                              px: 0.2,
                              alignSelf: isMine ? "flex-end" : "flex-start",
                            }}
                          >
                            <Typography sx={{ fontSize: 12, color: "#6b7280", fontWeight: 700 }}>
                              {messageLabel}
                            </Typography>
                            {!!messageTime && (
                              <Typography sx={{ fontSize: 12, color: "#9ca3af", fontWeight: 600 }}>
                                {messageTime}
                              </Typography>
                            )}
                          </Box>

                          <Box
                            sx={{
                              bgcolor: "transparent",
                              px: 0,
                              py: 0,
                            }}
                          >
                            {renderMessageContent(item)}
                          </Box>
                          {isSeenByStudent && (
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                                mt: 0.5,
                                px: 0.2,
                                alignSelf: "flex-end",
                              }}
                            >
                              <Typography sx={{ fontSize: 10, color: "#9ca3af" }}>{t("Seen")}</Typography> <VisibilityOutlinedIcon sx={{ fontSize: 14, color: "#9ca3af" }} />
                            </Box>
                          )}
                        </Box>
                      ) : (
                        <ChatBubble
                          isMine={isMine}
                          label={messageLabel}
                          time={messageTime}
                          seen={isSeenByStudent}
                          status={item.status}
                          progress={uploadProgress[item.id]}
                        >
                          {renderMessageContent(item)}
                        </ChatBubble>
                      );

                      return (
                        <Box
                          key={item.id}
                          sx={{
                            display: "flex",
                            justifyContent: isMine ? "flex-end" : "flex-start",
                            mb: 1.8,
                            gap: 1.2,
                            alignItems: "flex-end",
                            minWidth: 0, // IMPORTANT
                          }}
                        >
                          {/* Student avatar on the left (like your reference) */}
                          {!isMine && (
                            <Avatar
                              variant="square"
                              sx={{
                                width: 32,
                                height: 32,
                                bgcolor: bubbleTone.bg,
                                color: bubbleTone.color,
                                fontWeight: 800,
                                fontSize: 13,
                                borderRadius: 6,
                              }}
                            >
                              {initialsFromName(activeConversation?.studentName || t("Student"))}
                            </Avatar>
                          )}

                          {messageBody}

                          {isMine && (
                            <IconButton
                              size="small"
                              onClick={(event) => {
                                setMessageMenuAnchor(event.currentTarget);
                                setActiveMessageId(item.id);
                                setActiveMessageSenderId(item.senderId || null);
                              }}
                              aria-label={t("Message options")}
                              sx={{ mx: 0.5 }}
                            >
                              <MoreVertIcon fontSize="small" />
                            </IconButton>
                          )}
                        </Box>
                      );


                    })
                  )}

                  <div ref={messagesEndRef} />
                </Box>

                <Menu
                  anchorEl={messageMenuAnchor}
                  open={Boolean(messageMenuAnchor)}
                  onClose={() => {
                    setMessageMenuAnchor(null);
                    setActiveMessageId(null);
                    setActiveMessageSenderId(null);
                  }}
                  anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                  transformOrigin={{ vertical: "top", horizontal: "right" }}
                >
                  {activeMessageSenderId === teacherUid && (
                    <MenuItem
                      dense
                      onClick={async () => {
                        if (!activeConversation?.id || !activeMessageId || activeMessageSenderId !== teacherUid) {
                          setMessageMenuAnchor(null);
                          setActiveMessageId(null);
                          setActiveMessageSenderId(null);
                          return;
                        }
                        try {
                          await deleteDoc(
                            doc(db, "conversations", activeConversation.id, "messages", activeMessageId)
                          );
                          setMessages((prev) => prev.filter((m) => m.id !== activeMessageId));
                        } catch (err) {
                          console.error("Failed to delete message:", err);
                        } finally {
                          setMessageMenuAnchor(null);
                          setActiveMessageId(null);
                          setActiveMessageSenderId(null);
                        }
                      }}
                    >
                      {t("Delete message")}
                    </MenuItem>
                  )}
                </Menu>

                <Divider />

                {/* Composer (missing UI) */}
                <Box
                  sx={{
                    px: 2,
                    py: 1.5,
                    display: "flex",
                    flexDirection: isRtl ? "row-reverse" : "row",
                    alignItems: "center",
                    gap: 1,
                    backgroundColor: "#fff",
                  }}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*,video/*"
                    hidden
                    onChange={handleAttachmentChange}
                  />

                  <Tooltip title={t("Attach file")}>
                    <span>
                      <IconButton
                        size="small"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploading || recording}
                      >
                        <AttachFileRoundedIcon />
                      </IconButton>
                    </span>
                  </Tooltip>

                  <Tooltip title={recording ? t("Stop recording") : t("Voice message")}>
                    <span>
                      <IconButton
                        size="small"
                        color={recording ? "error" : "default"}
                        onClick={recording ? () => stopRecording() : startRecording}
                        disabled={uploading}
                      >
                        {recording ? <StopRoundedIcon /> : <KeyboardVoiceRoundedIcon />}
                      </IconButton>
                    </span>
                  </Tooltip>

                  <Popover
                    open={isEmojiPickerOpen}
                    anchorEl={emojiAnchorEl}
                    onClose={() => setEmojiAnchorEl(null)}
                    anchorOrigin={{ vertical: "top", horizontal: isRtl ? "right" : "left" }}
                    transformOrigin={{ vertical: "bottom", horizontal: isRtl ? "right" : "left" }}
                  >
                    <EmojiPicker
                      onEmojiClick={handleEmojiPick}
                      lazyLoadEmojis
                      skinTonesDisabled
                    />
                  </Popover>

                  {recording && (
                    <Chip
                      size="small"
                      label={t("{{count}}s / 120s", { count: recordSeconds })}
                      onDelete={cancelRecording}
                      deleteIcon={<CloseRoundedIcon />}
                      color="error"
                      variant="outlined"
                      sx={{ mx: 0.5 }}
                    />
                  )}

                  <TextField
                    inputRef={composerInputRef}
                    value={messageInput}
                    onChange={(e) => setMessageInput(e.target.value)}
                    fullWidth
                    placeholder={t("Write a message...")}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <Tooltip title={t("Emoji")}>
                            <span>
                              <IconButton
                                size="small"
                                onClick={(event) => setEmojiAnchorEl(event.currentTarget)}
                                disabled={uploading}
                              >
                                <EmojiEmotionsOutlinedIcon />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </InputAdornment>
                      ),
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        handleSend();
                      }
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 999,
                      },
                      "& .MuiOutlinedInput-input": {
                        textAlign: isRtl ? "right !important" : "left !important",
                        direction: isRtl ? "rtl !important" : "ltr !important",
                        unicodeBidi: "plaintext",
                        padding: "12px 16px",
                      },
                      "& .MuiOutlinedInput-input::placeholder": {
                        textAlign: isRtl ? "right" : "left",
                        opacity: 1,
                      },
                    }}
                  />

                  <Button
                    variant="contained"
                    disabled={sending || !messageInput.trim()}
                    onClick={handleSend}
                    sx={{
                      borderRadius: 999,
                      backgroundColor: "#2563eb",
                      whiteSpace: "nowrap",
                      px: 2,
                      fontWeight: 800,
                    }}
                  >
                    <SendIcon />
                  </Button>
                </Box>
              </>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

export default TeacherChatPage;
